import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sparkle, TrendUp, MapPin, Sun as SunIcon, CloudSun } from "@phosphor-icons/react";
import { fpPath } from "@/lib/site";

/**
 * FarmersHomeHero — the "Rhythm of Modern Farming" hero for
 * farmerspulse.com / naturepulse.net/farmers.
 *
 * Composition:
 *   - Hero image (green fields at sunset) with rounded corners
 *   - Centered headline + subhead
 *   - Two CTAs (Explore Live Pulse + Learn More)
 *   - Three feature mini-UI cards overlapping the hero image
 *
 * The palette is deliberately forest-green + cream — anchored to the
 * Farmers Pulse brand. Override the CSS var `--fp-brand` at the section
 * level to re-skin without touching each element.
 */
const HERO_IMG =
  "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2400&q=70";

export default function FarmersHomeHero({
  headline = "Know Your Farm. Know Your Weather. Know Your Market.",
  subhead = "The Living Agricultural Intelligence Network — a NaturePulse™ Partner, part of the SocialPulse® Network. Observe · Plan · Grow · Protect · Sell · Share.",
  primaryCta = { to: "/farms/new", label: "Set Up My Farm — Free" },
  secondaryCta = { to: "/ask", label: "Ask FarmersPulse AI" },
  heroImage = HERO_IMG,
}) {
  return (
    <section
      data-testid="farmers-home-hero"
      className="relative"
      style={{
        // Forest palette scoped to this section so we don't leak green
        // into the rest of the app.
        "--fp-brand": "#2D8659",
        "--fp-deep": "#0F3D2A",
        "--fp-cream": "#F8F4E9",
      }}
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-6">
        {/* Hero image with headline overlaid */}
        <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_-30px_rgba(15,61,42,0.4)]">
          <img
            src={heroImage}
            alt="Farmland at sunset"
            className="w-full h-[440px] md:h-[520px] object-cover"
            loading="eager"
          />
          {/* Soft cream wash so text stays readable regardless of image */}
          <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--fp-cream)]/70 via-[color:var(--fp-cream)]/25 to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center justify-start pt-14 md:pt-20 px-6 text-center">
            <h1
              className="font-serif-display leading-[0.98] tracking-tight max-w-4xl text-3xl sm:text-4xl md:text-5xl"
              style={{ color: "var(--fp-deep)" }}
              data-testid="farmers-home-hero-headline"
            >
              {headline}
            </h1>
            <p
              className="mt-3 font-mono-tech text-[10px] md:text-[11px] tracking-[0.28em] uppercase"
              style={{ color: "var(--fp-brand)" }}
              data-testid="farmers-home-hero-subbrand"
            >
              FarmersPulse™ · A NaturePulse™ Partner · Part of the SocialPulse® Network
            </p>
            <p
              className="mt-4 max-w-2xl text-base md:text-lg leading-relaxed"
              style={{ color: "var(--fp-deep)" }}
            >
              {subhead}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to={fpPath(primaryCta.to.replace(/^\/farmers/, "") || "/")}
                data-testid="farmers-home-hero-cta-primary"
                className="inline-flex items-center gap-1.5 rounded-full px-6 py-3 font-serif-display text-lg text-white shadow-sm hover:opacity-95 transition"
                style={{ backgroundColor: "var(--fp-brand)" }}
              >
                {primaryCta.label} <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to={fpPath(secondaryCta.to.replace(/^\/farmers/, "") || "/")}
                data-testid="farmers-home-hero-cta-secondary"
                className="inline-flex items-center gap-1.5 rounded-full border-2 px-6 py-3 font-serif-display text-lg bg-white/60 backdrop-blur hover:bg-white/80 transition"
                style={{ borderColor: "var(--fp-deep)", color: "var(--fp-deep)" }}
              >
                {secondaryCta.label}
              </Link>
            </div>
          </div>
        </div>

        {/* Feature mini-UI cards — overlap the hero by ~50% */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-5 -mt-16 md:-mt-20 relative z-10">
          <MarketPulseCard />
          <FieldHealthCard />
          <PrecisionForecastCard />
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------- */
/*  Feature card 1 — Market Pulse                                          */
/* ---------------------------------------------------------------------- */
function MarketPulseCard() {
  const rows = [
    { label: "Live Ticker", value: "$13.00", pct: "10.78%", up: true },
    { label: "Data Point",  value: "$108.00", pct: "3.58%", up: true },
    { label: "Low",         value: "$99.00",  pct: "3.56%", up: false },
    { label: "High",        value: "$154.00", pct: "0.89%", up: true },
  ];
  return (
    <FeatureCard
      icon={TrendUp}
      title="Market Pulse"
      body="Live agricultural intelligence, market trends, and field analytics."
      testid="farmers-home-card-market"
    >
      <div className="rounded-xl border border-black/5 overflow-hidden mb-3">
        <div className="flex items-center justify-between px-3 py-2 bg-[#F1F6F1] text-[#0F3D2A]">
          <div className="font-serif-display text-sm">Market Pulse</div>
          <div className="flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#2D8659]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2D8659] animate-pulse" /> Live Ticker
          </div>
        </div>
        <div className="grid grid-cols-4 divide-x divide-black/5 bg-white text-[10px]">
          {rows.map((r) => (
            <div key={r.label} className="px-2 py-2">
              <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#5A6A5C]">
                {r.label}
              </div>
              <div className="font-serif-display text-sm mt-0.5 text-[#0F3D2A]">{r.value}</div>
              <div className={`mt-0.5 font-mono-tech text-[9px] ${r.up ? "text-[#2D8659]" : "text-[#B85F2A]"}`}>
                {r.up ? "▲" : "▼"} {r.pct}
              </div>
            </div>
          ))}
        </div>
      </div>
    </FeatureCard>
  );
}

/* ---------------------------------------------------------------------- */
/*  Feature card 2 — Field Health (NDVI)                                   */
/* ---------------------------------------------------------------------- */
function FieldHealthCard() {
  return (
    <FeatureCard
      icon={MapPin}
      title="Field Health"
      body="Satellite NDVI maps, soil metrics and value scoring for sustainable growth."
      testid="farmers-home-card-field"
    >
      <div className="rounded-xl border border-black/5 overflow-hidden mb-3 grid grid-cols-[1fr_auto]">
        {/* Faux satellite NDVI patchwork — pure CSS so no image download */}
        <div className="relative bg-[#3F6C3A] h-[110px]">
          <div className="absolute inset-0 grid grid-cols-4 grid-rows-3">
            {[
              "#5E9C4D","#4F7F41","#3F6C3A","#2E5A2C",
              "#4F7F41","#5E9C4D","#7BB35C","#2E5A2C",
              "#3F6C3A","#2E5A2C","#5E9C4D","#4F7F41",
            ].map((c, i) => (
              <div key={i} style={{ backgroundColor: c, opacity: 0.85 + (i % 3) * 0.05 }} />
            ))}
          </div>
          {/* Warm-brown irregular patch, top-right, evokes "unhealthy zone" */}
          <div className="absolute right-3 top-3 h-6 w-10 rounded-md bg-[#B85F2A]/70 rotate-6" />
          <div className="absolute right-8 top-9 h-4 w-7 rounded-md bg-[#B85F2A]/50 -rotate-3" />
        </div>
        <div className="bg-white p-2 flex flex-col justify-between min-w-[92px]">
          <div>
            <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#5A6A5C]">
              Satellite NDVI
            </div>
            <div className="font-serif-display text-sm mt-0.5 text-[#2D8659]">68.75</div>
          </div>
          <div>
            <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#5A6A5C]">
              Soil Metrics
            </div>
            <div className="font-serif-display text-sm mt-0.5 text-[#0F3D2A]">0.35 <span className="text-[9px] text-[#5A6A5C]">%</span></div>
          </div>
        </div>
      </div>
    </FeatureCard>
  );
}

/* ---------------------------------------------------------------------- */
/*  Feature card 3 — Precision Forecast                                    */
/* ---------------------------------------------------------------------- */
function PrecisionForecastCard() {
  const days = [
    { label: "Weather", icon: CloudSun, temp: "28–13", low: "+2°C" },
    { label: "Thu",     icon: SunIcon,  temp: "22–13", low: "+2°C" },
    { label: "Sun",     icon: CloudSun, temp: "20–15", low: "+5°C" },
  ];
  return (
    <FeatureCard
      icon={SunIcon}
      title="Precision Forecast"
      body="Weather icons, weekly forecast chart, seasonal patterns and modern climate graphics."
      testid="farmers-home-card-forecast"
    >
      <div className="rounded-xl border border-black/5 overflow-hidden mb-3 grid grid-cols-[auto_1fr] gap-3 bg-white p-3">
        <div className="grid grid-cols-3 gap-2">
          {days.map((d) => {
            const Ic = d.icon;
            return (
              <div key={d.label} className="text-center">
                <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#5A6A5C]">{d.label}</div>
                <Ic size={22} weight="fill" className="mx-auto mt-1 text-[#2D8659]" />
                <div className="mt-0.5 font-mono-tech text-[9px] text-[#0F3D2A]">{d.temp}</div>
                <div className="font-mono-tech text-[8px] text-[#5A6A5C]">{d.low}</div>
              </div>
            );
          })}
        </div>
        <div>
          <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#5A6A5C]">Forecast</div>
          {/* Tiny inline SVG spark-line */}
          <svg viewBox="0 0 120 40" className="mt-1 w-full h-[52px]" aria-hidden>
            <defs>
              <linearGradient id="fp_fc" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2D8659" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#2D8659" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0 30 L20 22 L40 27 L60 15 L80 18 L100 8 L120 12 L120 40 L0 40 Z" fill="url(#fp_fc)" />
            <path d="M0 30 L20 22 L40 27 L60 15 L80 18 L100 8 L120 12" fill="none" stroke="#2D8659" strokeWidth="1.5" />
          </svg>
          <div className="flex justify-between font-mono-tech text-[8px] uppercase tracking-[0.15em] text-[#5A6A5C] px-0.5 mt-0.5">
            <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
          </div>
        </div>
      </div>
    </FeatureCard>
  );
}

/* ---------------------------------------------------------------------- */
/*  Shared card shell                                                       */
/* ---------------------------------------------------------------------- */
function FeatureCard({ icon: Icon, title, body, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-black/5 bg-white p-4 shadow-[0_10px_30px_-20px_rgba(15,61,42,0.35)]"
    >
      {children}
      <div className="flex items-center gap-2 text-[color:var(--fp-brand,#2D8659)]">
        <Icon size={18} weight="fill" />
        <div className="font-serif-display text-lg" style={{ color: "var(--fp-deep,#0F3D2A)" }}>
          {title}
        </div>
      </div>
      <p className="mt-1 text-sm" style={{ color: "#3E4A3F" }}>{body}</p>
    </div>
  );
}
